import {
  HTMLAttributes,
  SyntheticEvent,
  ChangeEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
  useId,
} from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import classNames from 'classnames';

import { useDebounce } from '../../hooks';
import { Box } from '../../Box';
import { Icon } from '../../Icon';
import { Checkbox } from '../../Checkbox';
import { CircularProgress } from '../../CircularProgress';
import { Tooltip } from '../../Tooltip';
import { Typography } from '../../Typography';
import { Label, Preloader, Badge } from '../common';
import { useAutocompleteReducer } from '../hooks';
import { Filter } from '../types';

import { ListBox } from './ListBox';
import useStyles from './MultipleAutocompleteInput.styles';
import {
  MultipleAutocompleteInputOption,
  MultipleAutocompleteInputProps,
} from './MultipleAutocompleteInput.types';

const DEBOUNCE_TIMEOUT = 200;

/**
 * Функция для получения отображаемых значений выбранных предзагруженных элементов.
 *
 * @param items - Массив доступных элементов.
 * @param value - Массив идентификаторов выбранных элементов.
 * @returns Строка с отображаемыми значениями выбранных элементов, разделенная запятыми.
 */
const getSelectedLoadValues = (
  items: MultipleAutocompleteInputOption[],
  value: string[] | undefined,
): string =>
  items
    .filter((item) => value?.includes(item.value))
    .map((item) => `${item.prefix ? `${item.prefix}, ` : ''}${item.label}`)
    .join(', ') || '';

/**
 * Компонент для ввода нескольких файлов.
 *
 * @public
 * @param id - Атрибут ID для поля ввода.
 * @param value - Текущее значение поля ввода (массив идентификаторов выбранных файлов).
 * @param onChange - Функция обратного вызова для обновления значения поля ввода.
 * @param onBlur - Функция обратного вызова при потере фокуса.
 * @param onLogAction - Функция обратного вызова для логирования действий.
 * @param label - Метка для поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Текст подсказки для метки поля ввода.
 * @param name - Имя поля ввода.
 * @param placeholder - Текст плейсхолдера для поля ввода.
 * @param required - Обязательное поле ввода.
 * @param limit - Лимит элементов для загрузки.
 * @param disabledTooltip - Текст подсказки для отключенных элементов.
 * @param disabled - Флаг, указывающий на то, что поле ввода отключено.
 * @param clearable - Флаг, указывающий на возможность очистки выбранного значения.
 * @param loading - Флаг, указывающий на то, что идет загрузка.
 * @param error - Флаг, указывающий на наличие ошибки.
 * @param helperText - Вспомогательный текст для поля ввода.
 * @param minInputValueLength - Минимальная длина ввода для начала поиска.
 * @param renderOption - Функция для рендера опций выбранных элементов.
 * @param renderTags - Функция для рендера тегов выбранных элементов.
 * @param testId - Идентификатор для тестирования.
 * @param withoutMargin - Флаг, указывающий на отсутствие отступов.
 * @param additionalFilters - Дополнительные фильтры для запроса элементов.
 * @param type - Тип загрузки элементов: 'load' - динамическая загрузка, 'local' - локальные элементы.
 * @param loadItems - Функция для запроса элементов (только для type='load').
 * @param externalItems - Внешние элементы (файлы), которые могут быть выбраны (только для type='local').
 * @param selectAllLabel - Текст метки для выбора всех элементов (только для type='local').
 * @param allowSelectAll - Флаг, указывающий на возможность выбора всех элементов (только для type='local').
 * @returns Элемент MultipleFileInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link MultipleAutocompleteInputProps}
 */
export const MultipleAutocompleteInput = (
  props: MultipleAutocompleteInputProps,
) => {
  const {
    id,
    value: initialValue,
    onChange,
    onBlur,
    onLogAction,
    label,
    labelIcon,
    tooltip,
    name,
    placeholder = '',
    required = false,
    limit = 50,
    disabledTooltip = 'Недоступен для выбранного типа заявки',
    disabled = false,
    clearable = true,
    loading = false,
    error = false,
    helperText = '',
    minInputValueLength = 1,
    renderTags,
    testId = 'test-id-multiple-autocomplete-input',
    additionalFilters,
    withoutMargin = false,
    renderOption,
  } = props;

  const stringifiedValue = JSON.stringify(
    initialValue ? [...initialValue].sort() : undefined,
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(() => initialValue, [stringifiedValue]);

  const { classes } = useStyles();
  const reactId = useId();
  const inputId = useMemo(() => {
    return id ?? reactId;
  }, [id, reactId]);

  const { items, status, hasMore, requestItems } =
    useAutocompleteReducer<MultipleAutocompleteInputOption>(
      props.type === 'load' ? props.loadItems : undefined,
    );

  const [inputValue, setInputValue] = useState('');
  const [selectedValues, setSelectedValues] = useState(
    getSelectedLoadValues(
      props.type === 'local' ? props.externalItems : items,
      value,
    ),
  );
  const [allSelectedItems, setAllSelectedItems] = useState<
    MultipleAutocompleteInputOption[]
  >(
    props.type === 'local'
      ? props.externalItems.filter((item) => value?.includes(item.value))
      : [],
  );
  const [isFirstRenderDone, setIsFirstRenderDone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>({
    limit,
    offset: 0,
    query: '',
  });
  const [open, setOpen] = useState(false);
  const offsetRef = useRef(0);
  const debouncedValue = useDebounce(inputValue, DEBOUNCE_TIMEOUT);
  const inputValueForUse =
    inputValue.length < minInputValueLength ? inputValue : debouncedValue;
  const isThereSomeGroup = items.some((option) => option.group);
  const isItemsLoading = isLoading || status === 'loading';

  const autocompleteGetOptions = () => {
    if (props.type === 'local') return props.externalItems;

    if (!offsetRef.current && isItemsLoading) {
      return [];
    }

    return items;
  };

  const currentItems = autocompleteGetOptions();

  const renderedItems = props.type === 'load' ? allSelectedItems : currentItems;

  const autocompleteOptions = useMemo(() => currentItems, [currentItems]);

  const autocompleteGetInputValue = () => {
    if (loading || !isFirstRenderDone) {
      return 'Загрузка...';
    } else if (!open && !inputValue) {
      return selectedValues;
    }

    return inputValue;
  };

  const autocompleteOpenHandler = () => {
    onLogAction?.('Открыть выпадающий список');
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setSelectedValues(getSelectedLoadValues(renderedItems, value));
  };

  const autocompleteCloseHandler = () => {
    close();
  };

  const autocompleteBlurHandler = () => {
    if (open) {
      close();
    }

    onBlur?.();
  };

  const autocompleteGetOptionLabel = (
    option: MultipleAutocompleteInputOption,
  ) => `${option.prefix ? `${option.prefix}, ` : ''}${option.label}`;

  const autocompleteGetOptionSelected = (
    option: MultipleAutocompleteInputOption,
    selected: MultipleAutocompleteInputOption,
  ) => option.value === selected.value;

  const autocompleteGetOptionDisabled = (
    option: MultipleAutocompleteInputOption,
  ) => option.disabled || false;

  const autocompleteFocusHandler = () => {
    setOpen(true);
  };

  const autocompleteChangeHandler = (
    event: SyntheticEvent,
    nextValue: MultipleAutocompleteInputOption[],
    reason: string,
  ) => {
    event.stopPropagation();

    if (props.type === 'local') {
      onChange(nextValue.map((item) => item.value));
      setAllSelectedItems(nextValue);
    } else {
      let valuesToSet: string[] = [];

      if (reason === 'selectOption') {
        valuesToSet = [
          ...(value || []),
          ...nextValue
            .filter((item) => !value?.includes(item.value))
            .map((item) => item.value),
        ];
        setAllSelectedItems((prev) => [
          ...prev,
          ...nextValue.filter(
            (item) =>
              !prev.find(({ value: itemValue }) => itemValue === item.value),
          ),
        ]);
      } else if (reason === 'removeOption') {
        const valuesToRemove = allSelectedItems
          .filter(
            (item) =>
              value?.includes(item.value) &&
              !nextValue.find(
                ({ value: itemValue }) => itemValue === item.value,
              ),
          )
          .map((item) => item.value);
        valuesToSet = [
          ...(value || []).filter((item) => !valuesToRemove.includes(item)),
        ];
        setAllSelectedItems((prev) =>
          prev.filter((item) => valuesToSet.includes(item.value)),
        );
      }

      onChange(valuesToSet);
    }
  };

  const autocompleteClearHandler = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    onChange([]);
    setAllSelectedItems([]);
  };

  const autocompleteInputChangeHandler = (
    event: SyntheticEvent,
    nextValue: string,
    reason: string,
  ) => {
    if (
      reason === 'reset' ||
      reason === 'selectOption' ||
      reason === 'removeOption'
    ) {
      if (
        !event ||
        (event as ChangeEvent<HTMLInputElement>)?.target.closest(
          'div#scrollable',
        )
      )
        return;
    } else {
      setInputValue(nextValue);
    }

    if (reason === 'clear') {
      setSelectedValues('');
    }

    if (reason === 'input' && props.type === 'load') {
      setIsLoading(true);
      offsetRef.current = 0;
      setFilter({
        ...filter,
        offset: 0,
        query: nextValue,
      });
    }
  };

  const autocompleteSelectAllChangeHandler = () => {
    if (props.type === 'load') return;

    if (
      value &&
      currentItems.filter((item) => !item.disabled).length === value.length
    ) {
      onChange([]);
      setSelectedValues('');
    } else {
      const itemsToSelect = currentItems
        .filter((item) => !item.disabled)
        .sort((a, b) => (a.label > b.label ? 1 : -1));

      onChange(itemsToSelect.map((item) => item.value));
      setSelectedValues(
        itemsToSelect
          .map(
            (item) => `${item.prefix ? `${item.prefix}, ` : ''}${item.label}`,
          )
          .join(', '),
      );
    }
  };

  useEffect(() => {
    if (props.type === 'local') return;

    if (
      !open ||
      (inputValueForUse !== '' && inputValueForUse.length < minInputValueLength)
    ) {
      return;
    }

    offsetRef.current = 0;
    setFilter({
      limit,
      offset: 0,
      query: inputValueForUse,
    });

    const abortRequest = requestItems?.(
      {
        ...additionalFilters,
        offset: 0,
        limit,
        query: inputValueForUse,
      },
      'search',
    );
    setIsLoading(false);

    return () => {
      abortRequest?.();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inputValueForUse]);

  useEffect(() => {
    (async () => {
      if (value?.length && !open && props.type === 'load' && !selectedValues) {
        const itemsByIds = await props.loadItems?.({
          limit: value.length,
          offset: 0,
          query: '',
          ids: value,
          ...additionalFilters,
        });

        const sortedItems = itemsByIds?.sort((a, b) =>
          a.label > b.label ? 1 : -1,
        );

        if (sortedItems)
          setSelectedValues(getSelectedLoadValues(sortedItems, value));

        setAllSelectedItems(sortedItems || []);
      }

      setIsFirstRenderDone(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectedValues]);

  useEffect(() => {
    setSelectedValues(
      getSelectedLoadValues(
        props.type === 'local' ? props.externalItems : allSelectedItems,
        value,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const defaultRenderOption = (
    optionProps: HTMLAttributes<HTMLLIElement>,
    option: MultipleAutocompleteInputOption,
  ) => {
    const { badge, additionalText } = option;
    const isBottom = badge?.position === 'bottom';

    return (
      <li {...optionProps} key={option.value}>
        <div
          className={classes.listItem}
          onClick={(event) => option.disabled && event.stopPropagation()}
        >
          <Box flexShrink={0}>
            <Checkbox
              withoutMargin
              sizeVariant="large"
              checked={
                !!value && value.some((item: string) => item === option.value)
              }
            />
          </Box>
          {badge && (
            <Box mr="8" order={isBottom ? 1 : 0} width={64} flexShrink={0}>
              <Badge
                title={badge.title}
                width="100%"
                description={badge.description}
                color={badge.color}
                size="extraSmall"
              />
            </Box>
          )}
          {option.disabled ? (
            <div className={classes.listItemLabel}>
              <Tooltip
                title={`${disabledTooltip}${
                  option.tooltipText ? ` ${option.tooltipText}` : ''
                }`}
              >
                <div>
                  {option.prefix ? `${option.prefix}, ` : ''}
                  {option.label}
                </div>
              </Tooltip>
            </div>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              gap="4"
              maxWidth="calc(100% - 42px)"
            >
              <div className={classes.defaultWordBreak}>
                {option.prefix ? `${option.prefix}, ` : ''}
                {option.label}
              </div>

              {additionalText && (
                <Typography variant="caption" color="text.light.tertirary">
                  {option.additionalText}
                </Typography>
              )}
            </Box>
          )}
        </div>
      </li>
    );
  };

  const customListboxProps = {
    dataLength: currentItems.length,
    next: () => {
      // Блокируем подгрузку если идёт загрузка (в т.ч. начальная)
      if (status === 'loading') {
        return;
      }

      const currentOffset = offsetRef.current;
      const nextOffset = currentOffset + limit;

      offsetRef.current = nextOffset;

      requestItems?.(
        {
          ...additionalFilters,
          offset: nextOffset,
          limit,
          query: filter.query,
        },
        'get',
      );

      setFilter((prevFilter) => ({
        ...prevFilter,
        offset: nextOffset,
      }));
    },
    hasMore: props.type === 'load' ? hasMore : false,
    loading: isItemsLoading,
  };

  return (
    <Box
      className={classNames(
        classes.multipleAutocompleteInput,
        !withoutMargin && classes.defaultMargin,
      )}
    >
      {label && (
        <Label
          label={label}
          required={required}
          tooltip={tooltip}
          icon={labelIcon}
          inputId={inputId}
        />
      )}
      <Autocomplete<MultipleAutocompleteInputOption, true, boolean>
        disableClearable
        data-testid={testId}
        filterOptions={props.type === 'load' ? (options) => options : undefined}
        open={open}
        id={inputId}
        onOpen={autocompleteOpenHandler}
        onClose={autocompleteCloseHandler}
        onFocus={autocompleteFocusHandler}
        getOptionLabel={autocompleteGetOptionLabel}
        onChange={autocompleteChangeHandler}
        onBlur={autocompleteBlurHandler}
        classes={{
          paper: classes.paper,
          option: classes.option,
          listbox: classes.listbox,
        }}
        disabled={disabled || loading}
        multiple
        disableCloseOnSelect
        clearOnEscape
        popupIcon={
          <Box m="4">
            <Icon
              name="arrow_triangle_down"
              size="16"
              color="text.dark.secondary"
            />
          </Box>
        }
        options={autocompleteOptions}
        noOptionsText="Ничего не найдено..."
        value={renderedItems.filter((item) => value?.includes(item.value))}
        defaultValue={[]}
        inputValue={autocompleteGetInputValue()}
        onInputChange={autocompleteInputChangeHandler}
        loading={isItemsLoading}
        loadingText={
          <Box display="flex" justifyContent="center" p="16">
            <CircularProgress size={30} />
          </Box>
        }
        renderOption={
          renderOption
            ? (optionProps, option) => (
                <li {...optionProps} key={option.value}>
                  {renderOption(option, value)}
                </li>
              )
            : defaultRenderOption
        }
        {...(isThereSomeGroup
          ? {
              groupBy: ({ group }) => {
                if (group) {
                  return group;
                }

                return 'Заявки без группировки по коллекциям';
              },
            }
          : {})}
        renderGroup={(params) => (
          <>
            <Box px="16" pt="8" pb="4">
              <Typography
                component="span"
                variant="caption"
                color="text.light.tertirary"
              >
                {params.group}
              </Typography>
            </Box>
            <Box>{params.children}</Box>
          </>
        )}
        isOptionEqualToValue={autocompleteGetOptionSelected}
        getOptionDisabled={autocompleteGetOptionDisabled}
        slotProps={{
          listbox: {
            component: ListBox,
            ...customListboxProps,
          },
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              name={name}
              classes={{ root: classes.input }}
              variant="outlined"
              autoComplete="off"
              fullWidth
              disabled={disabled || loading || !isFirstRenderDone}
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              slotProps={{
                input: {
                  className: params?.InputProps?.className,
                  ref: params?.InputProps?.ref,
                  endAdornment:
                    loading || !isFirstRenderDone ? (
                      <Preloader />
                    ) : (
                      <Box display="flex" alignItems="center" mx="16" gap="4">
                        {clearable &&
                          !disabled &&
                          !!value?.length &&
                          !!selectedValues && (
                            <Box width={24} height={24}>
                              <Box
                                className={`${classes.clearIcon} multipleAutocompleteClearIcon`}
                                onClick={autocompleteClearHandler}
                              >
                                <Icon
                                  name="dismiss_substract"
                                  color="stroke.secondary"
                                  size="24"
                                />
                              </Box>
                            </Box>
                          )}

                        <Box>{params?.InputProps?.endAdornment}</Box>
                      </Box>
                    ),
                },
              }}
            />
          );
        }}
      />
      {props.type === 'local' && props.allowSelectAll && (
        <FormControlLabel
          control={
            <Checkbox
              withoutMargin
              sizeVariant="large"
              disabled={disabled}
              checked={
                !!value?.length &&
                currentItems.filter((item) => !item.disabled).length ===
                  value.length
              }
              onChange={autocompleteSelectAllChangeHandler}
            />
          }
          label={
            <Typography variant="body3" color="text.light.secondary">
              {props.selectAllLabel || 'Выбрать всех'}
            </Typography>
          }
        />
      )}
      {renderTags && !disabled && !!value?.length && (
        <Box mt="16">
          {renderTags(
            value,
            props.type === 'load' ? allSelectedItems : props.externalItems,
          )}
        </Box>
      )}
    </Box>
  );
};
